/**
 * A rank check's alerts, put on the searches they are about. Pure, so CRA's
 * jest can test it.
 */
import type { WatchEvent, WatchRunSearches } from '../../types';

export type SearchRow = WatchRunSearches['searches'][number] & { events: WatchEvent[] };

/** Is this alert from a rank check? */
export function isRankEvent(e: WatchEvent): boolean {
  return (e.probe_kinds ?? []).includes('serp_rank');
}

/** "#5", "not in top 10", or "-" before the search was watched. Pure. */
export function positionWords(pos: number | null, compared = true): string {
  if (!compared) return '-';
  return pos === null ? 'not in top 10' : `#${pos}`;
}

/**
 * Each search with the alerts about it, and the alerts about the whole set (a
 * new competitor) apart. An alert finds its search by `keyword_id`; one from
 * before that was kept, by its query. Rows with an alert or a change come
 * first. Pure.
 */
export function searchRows(
  data: WatchRunSearches,
  events: WatchEvent[],
): { rows: SearchRow[]; acrossSet: WatchEvent[] } {
  const rows: SearchRow[] = data.searches.map(s => ({ ...s, events: [] }));
  const acrossSet: WatchEvent[] = [];
  for (const e of events) {
    const kid = Number(e.payload?.keyword_id) || 0;
    const query = String(e.payload?.query ?? '');
    const row = kid ? rows.find(r => r.keyword_id === kid) : query ? rows.find(r => r.query === query) : undefined;
    if (row) row.events.push(e);
    else acrossSet.push(e);
  }
  const quiet = (r: SearchRow) => Number(!(r.events.length || r.changed));
  rows.sort((a, b) => quiet(a) - quiet(b));
  return { rows, acrossSet };
}

const bare = (d: string) => d.toLowerCase().replace(/^www\./, '');

/**
 * The searches a domain new across the set shows up on: the ones the alert
 * kept, or - on an alert from before they were kept - read off the check's own
 * results. Pure.
 */
export function competitorQueries(e: WatchEvent, rows: { query: string; domains?: string[] }[]): string[] {
  if (Array.isArray(e.payload?.queries)) return e.payload.queries as string[];
  const domain = bare(String(e.payload?.detail ?? ''));
  if (!domain) return [];
  return rows.filter(r => (r.domains ?? []).some(d => bare(d) === domain)).map(r => r.query);
}

/** "1,300", "under 10" (the search tools say 0 below that), or "not looked up". Pure. */
export function volumeWords(n: number | null): string {
  if (n === null || n === undefined) return 'not looked up';
  return n === 0 ? 'under 10' : n.toLocaleString('en-US');
}

/** "~48 → ~0", "~12", or "" when there is no search count to go on. Pure. */
export function visitsWords(was: number | null, now: number | null): string {
  if (now === null || now === undefined) return '';
  if (was === null || was === undefined || was === now) return `~${now}`;
  return `~${was} → ~${now}`;
}
